import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { ccyFormat, dateFormat } from '../../helpers/helper';
import { STATUS_CONFIG, printerTypeLabel } from './ticketHelpers';

const TicketDetailModal = ({
  job,
  open,
  onClose,
  onReprint,
  onDownload,
  reprinting,
  downloading,
}) => {
  if (!job) return null;

  const status = STATUS_CONFIG[job.status] || STATUS_CONFIG.printed;
  const items = Array.isArray(job.items) ? job.items : [];
  const payments = Array.isArray(job.payments) ? job.payments : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" fontWeight={700}>
                Ticket #{job.ticket_number || '—'}
              </Typography>
              <Chip label={status.label} color={status.color} size="small" />
            </Stack>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {job.customer || 'Consumidor final'} · {job.printed_at ? dateFormat(job.printed_at) : '—'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {job.status === 'failed' && job.error_msg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {job.error_msg}
          </Alert>
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          mb={2}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">Impresora</Typography>
            <Typography variant="body2" fontWeight={600}>
              {job.printer_name || '—'} ({printerTypeLabel(job.printer_type)})
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Reimpresiones</Typography>
            <Typography variant="body2" fontWeight={600}>{job.print_count ?? 0}</Typography>
          </Box>
        </Stack>

        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><strong>Cant.</strong></TableCell>
                <TableCell><strong>Producto</strong></TableCell>
                <TableCell><strong>SKU</strong></TableCell>
                <TableCell align="right"><strong>P. Unit.</strong></TableCell>
                <TableCell align="right"><strong>Total</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Sin ítems
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {items.map((item, i) => (
                <TableRow key={`${item.sku || item.name}-${i}`}>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{item.name}</Typography>
                    {item.note && (
                      <Typography variant="caption" color="text.secondary">{item.note}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{item.sku || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">${ccyFormat(item.price || 0)}</TableCell>
                  <TableCell align="right">${ccyFormat(item.total || 0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack spacing={0.5} mt={2} alignItems="flex-end">
          <Stack direction="row" spacing={4}>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'right' }}>
              ${ccyFormat(job.subtotal || 0)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={4}>
            <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
            <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: 120, textAlign: 'right' }}>
              ${ccyFormat(job.total || 0)}
            </Typography>
          </Stack>
        </Stack>

        {payments.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>Pagos</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {payments.map((payment, i) => (
                <Chip
                  key={`${payment.method}-${i}`}
                  label={`${payment.method}: $${ccyFormat(payment.amount || 0)}`}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {job.output_path && (
          <Button
            startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={() => onDownload?.(job)}
            disabled={downloading}
          >
            Descargar PDF
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={reprinting ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
          onClick={() => onReprint?.(job)}
          disabled={reprinting}
        >
          Reimprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketDetailModal;
